/**
 * Poses in play (spec 05 §6.2): what a figure is doing, read from the simulation, turned into a
 * pose. Poses move continuously, at the display's rate, as the figures glide between sim frames
 * (the owner found 12 Hz sprite-like steps clunky on a model, 2026-09-24); figures turn round rather
 * than flip, and a dragon's walk cycle follows the ground it covers, so its feet do not slide.
 */

import { monsterPose } from '../../sim/monsters';
import { playerPose } from '../../sim/players';
import type { Monster, Player } from '../../sim/types';
import type { Puppet } from '../actors/puppet';

/** World units a dragon covers in one walk cycle (2 px an I-tick is 6.25 units/s; ~2.6 steps/s). */
const WALK_STRIDE = 2.4;

export type DragonPose = 'idle' | 'walk' | 'jump' | 'fall' | 'blow' | 'dying';

/**
 * Put a dragon in `pose` at time `t` (seconds), turning to face `dir`; `walkPhase` (radians)
 * drives the walk cycle when given, else time does.
 */
export function poseDragon(pup: Puppet, pose: DragonPose, t: number, dir: 1 | -1, walkPhase?: number): void {
  const s = t;
  pup.turnTo(dir, t);
  pup.part('mouth').visible = pose === 'blow';
  pup.root.rotation.z = 0;
  const ph = walkPhase ?? s * Math.PI * 2 * 2.5;
  switch (pose) {
    case 'walk':
      pup.pose('footNear', Math.sin(ph) * 0.16, Math.max(0, Math.cos(ph)) * 0.08);
      pup.pose('footFar', -Math.sin(ph) * 0.16, Math.max(0, -Math.cos(ph)) * 0.08);
      pup.pose('body', 0, Math.abs(Math.sin(ph)) * 0.05).rotation.z = -0.05;
      pup.pose('head').rotation.z = Math.sin(ph) * 0.04;
      pup.pose('armNear', Math.sin(ph) * -0.06);
      pup.pose('armFar', Math.sin(ph) * 0.06);
      return;
    case 'jump':
    case 'fall': {
      // Feet tucked going up, dangling coming down; arms up either way.
      const up = pose === 'jump';
      pup.pose('footNear', 0.06, up ? 0.12 : -0.02);
      pup.pose('footFar', -0.06, up ? 0.08 : -0.04);
      pup.pose('body').rotation.z = up ? 0.08 : -0.06;
      pup.pose('head').rotation.z = up ? 0.06 : -0.04;
      pup.pose('armNear', 0.02, 0.12);
      pup.pose('armFar', 0.02, 0.12);
      return;
    }
    case 'blow':
      pup.pose('footNear');
      pup.pose('footFar');
      pup.pose('body', 0.04).rotation.z = -0.1;
      pup.pose('head', 0.06, 0.02).rotation.z = 0.12;
      pup.pose('armNear', -0.08, 0.04);
      pup.pose('armFar', -0.08, 0.04);
      return;
    case 'dying':
      // Spinning, as the sprite does.
      pup.facing.rotation.y = s * 12;
      pup.pose('footNear');
      pup.pose('footFar');
      pup.pose('body');
      pup.pose('head');
      pup.pose('armNear', 0, 0.2);
      pup.pose('armFar', 0, 0.2);
      return;
    default: {
      const breathe = Math.sin(s * Math.PI * 2 * 1.2) * 0.02;
      pup.pose('footNear');
      pup.pose('footFar');
      pup.pose('armNear');
      pup.pose('armFar');
      pup.pose('body', 0, breathe);
      pup.pose('head', 0, breathe * 0.5);
    }
  }
}

/**
 * A player's pose from the sim's own reading of it (`playerPose`, the PLAYER stream's name for the
 * C64 sprite frame): stand, walk, jump, fall, blow, dying. The death spin lasts the first 32 ticks,
 * then he lies still.
 */
export function poseDragonFor(pup: Puppet, p: Player, t: number): void {
  const ps = playerPose(p);
  const walkedRecently = pup.root.userData['walkUntil'] !== undefined && t < (pup.root.userData['walkUntil'] as number);
  let pose: DragonPose;
  switch (ps.pose) {
    case 'walk': pose = 'walk'; break;
    case 'jump': pose = 'jump'; break;
    case 'fall': pose = 'fall'; break;
    case 'blow': pose = 'blow'; break;
    case 'dying': pose = ps.deathTick < 32 ? 'dying' : 'idle'; break;
    default: pose = walkedRecently ? 'walk' : 'idle';
  }
  // Walking is reported on the ticks that move; hold the cycle between them so it does not flicker.
  if (ps.pose === 'walk') pup.root.userData['walkUntil'] = t + 0.1;
  poseDragon(pup, pose, t, ps.facing, stride(pup) * ((Math.PI * 2) / WALK_STRIDE));
  if (ps.pose === 'dying' && ps.deathTick >= 32) {
    // Lying where he fell.
    pup.root.rotation.z = ps.facing === 1 ? Math.PI / 2 : -Math.PI / 2;
  }
}

/** Ground covered since the puppet was made (world units): the root's travel, frame to frame. */
function stride(pup: Puppet): number {
  const d = pup.root.userData;
  const x = pup.root.position.x;
  const last = d['lastX'];
  const moved = typeof last === 'number' ? Math.abs(x - last) : 0;
  d['lastX'] = x;
  // A respawn is not a step.
  const total = (typeof d['stride'] === 'number' ? d['stride'] : 0) + (moved < 1 ? moved : 0);
  d['stride'] = total;
  return total;
}

/**
 * A monster's pose from the sim (`monsterPose`): walking steps (the C64's walk cycle plays while it
 * waits, too), a crouch squashes it before a vertical jump, jumping stretches it, falling lifts its
 * feet; flyers bob; Banebou's spring works; a trapped one hangs still in its bubble.
 */
export function poseMonster(pup: Puppet, m: Monster, t: number): void {
  pup.turnTo(m.facing, t);
  const pose = monsterPose(m);
  const s = t + m.id * 0.13;
  const ph = s * Math.PI * 2 * (m.angry ? 3.5 : 2.4);
  const parts = pup.parts;
  const stepping = pose === 'walk' || pose === 'wait' || pose === 'dropIn' ? 1 : 0;
  const tucked = pose === 'jump' || pose === 'fall' || pose === 'escape';
  if (parts.has('footNear')) {
    pup.pose('footNear', Math.sin(ph) * 0.14 * stepping, tucked ? 0.1 : Math.max(0, Math.cos(ph)) * 0.06 * stepping);
    pup.pose('footFar', -Math.sin(ph) * 0.14 * stepping, tucked ? 0.06 : Math.max(0, -Math.cos(ph)) * 0.06 * stepping);
  }
  const squash = pose === 'crouch' ? 0.8 : pose === 'jump' ? 1.1 : 1;
  pup.facing.scale.y = squash;
  if (parts.has('body')) {
    const bob = pose === 'fly' ? Math.sin(ph * 0.5) * 0.08 : stepping * Math.abs(Math.sin(ph)) * 0.05;
    pup.pose('body', 0, pose === 'trapped' ? 0 : bob);
  }
  if (parts.has('key')) pup.pose('key').rotation.x = pose === 'trapped' ? 0 : s * 8;
  if (parts.has('prop')) pup.pose('prop').rotation.y = s * 10;
  if (parts.has('spring')) pup.part('spring').scale.y = 1 + Math.sin(ph) * 0.25;
}
